import React, { createContext, useState, useEffect, useContext } from "react";
import CookieReader from 'Utilities/CookieUtilties/CookieReader'
import useSegment from "Hooks/useSegment"
import { createViewedSegmentEvent, createClickedSegmentEvent } from "../../Utilities/Instrumentation/Impressions/impressionSegmentEvents";
import { NavigationContext } from 'Contexts/Navigation/NavigationContext'
import { isInApp } from "../../Hooks/useAppInstall/AppUtilities";
import { StorageAccessor } from "../../Utilities/LocalStorageUtility/LocalStorageUtility";

const initialState = {
  googleAnalyticsReady: false,
  bloomReachReady: false,
  segmentReady: false,
  ga4SessionData: null,
  shouldRenderBloomreach: false,
  appInstallPrompt: null,
  ga4ContentGroup: null
};


const TrackingContext = createContext(initialState);

const TrackingProvider = ({ children }) => {
  const { state: navigationState, dispatch: navigationDispatch } = useContext(NavigationContext)
  const [context, setContext] = useState(initialState);
  const { sendCustomSegmentTrackEvent } = useSegment()
  const [eventQueue, updateEventQueue] = useState([])
  const [virtualPageEventQueue, updateVirtualPageEventQueue] = useState([])

  //#region google analytics
  const setGoogleAnalyticsReady = () => {
    setContext((state) => {
      return {
        ...state,
        googleAnalyticsReady: true,
      };
    });
  };
  //#endregion

  const setSegmentReady = () => {
    setContext((state) => {
      return {
        ...state,
        segmentReady: true,
      };
    });
  };

  const setBloomReachReady = () => {
    setContext((state) => {
      return {
        ...state,
        bloomReachReady: true,
      };
    });
  };

  const setGa4SessionData = (ga4SessionData) => {
    setContext((state) => {
      return {
        ...state,
        ga4SessionData
      };
    });
  };

  const setGa4ContentGroup = (ga4ContentGroup) => {
    setContext((state) => {
      return {
        ...state,
        ga4ContentGroup
      };
    });
  };

  const setShouldRenderBloomreach = () => {
    setContext((state) => {
      return {
        ...state,
        shouldRenderBloomreach: true
      };
    });
  };

  const getBloomreachUserId = () => CookieReader('WebUserGuid')

  const bloomreachInitialPageView = (event) => {
    event.acct_id = '6357'
    event.user_id = getBloomreachUserId()
    event.view_id = 'BASE'
    try {
      if (!window.br_data) {
        window.br_data = event
        setShouldRenderBloomreach()
      }
    } catch (err) {
      console.error("error sending bloomreach initial page event", err)
      return
    }
  }

  const bloomreachVirtualPageView = (event) => {
    event.acct_id = '6357'
    event.user_id = getBloomreachUserId()
    try {
      if (!event.orig_ref_url) {
        event.orig_ref_url = window.location.href
      }
    }
    catch (err) {
      console.error("There was a problem setting the br virtual page view orig ref url")
    }

    try {
      updateVirtualPageEventQueue(prevItems => [...prevItems, { ...event }])

    } catch (err) {
      console.error("error sending bloomreach virtual page event", err)
      return
    }
  }

  const bloomreachEvent = (event) => {
    updateEventQueue(prevItems => [...prevItems, { ...event }]);
  }

  //events
  useEffect(() => {
    if (context.bloomReachReady) {
      if (eventQueue.length) {
        eventQueue.forEach(event => {
          try {
            window.BrTrk.getTracker().logEvent(event.ptype, event.action, event.data)
          } catch (err) {
            console.error("error sending bloomreach event", err)
          }
        })
        updateEventQueue([])
      }
    }
    else if (eventQueue.length) {
      setShouldRenderBloomreach()
    }
  }, [context.bloomReachReady, eventQueue])

  //virtual Page Events
  useEffect(() => {
    if (context.bloomReachReady) {
      if (virtualPageEventQueue.length) {
        virtualPageEventQueue.forEach(event => {
          try {
            window.BrTrk.getTracker().updateBrData(event)
            window.BrTrk.getTracker().logPageView()

          } catch (err) {
            console.error("error sending bloomreach virtual page event", err)
          }
        })
        updateVirtualPageEventQueue([])
      }
    }
    else if (virtualPageEventQueue.length) {
      setShouldRenderBloomreach()
    }
  }, [context.bloomReachReady, virtualPageEventQueue])


  const [isNavigating, setIsNavigating] = useState(false)

  const toggleNavigatingState = (forceState = null) => {
    if (forceState !== null) {
      setIsNavigating(!!forceState);
      return;
    }
    setIsNavigating(!isNavigating);
  }

  const handleServiceWorkerMessages = (event) => {
    try {
      const message = event.data
      console.log(`Message from service worker`, message)
    } catch (err) {
      console.error(`Error handling service worker message`, err)
    }
  }

  const fireIsInAppEvent = () => {
    if (!window.matchMedia('(display-mode: browser)').matches) {
      const launchText = `PWA in use`;
      const event = createViewedSegmentEvent(launchText);
      analytics.track(event.event, event.properties)
      console.log(launchText);
    }
  }

  const setupServiceWorker = async () => {
    try {
      fireIsInAppEvent();
      const allServiceWorkers = await navigator.serviceWorker.getRegistrations();

      for (let reg of allServiceWorkers) {
        await reg.unregister();
      }
      const now = new Date();
      navigator.serviceWorker.register(`/service-worker.js?v=${now.toDateString()} ${now.getHours()} ${now.getMinutes()}`).then(
        function (registration) {
          console.log(
            "Service Worker registration successful with scope: ",
            registration.scope
          );
          navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessages);
        },
        function (err) {
          console.error("Service Worker registration failed: ", err);
        }
      );
    } catch (err) {
      console.error("Service worker setup failed", err);
    }


  }

  const prefix = `Native app install prompt`
  useEffect(() => {
    toggleNavigatingState(false);

    const beforeInstallPrompt = (e) => {

      e.preventDefault();

      setContext((state) => {
        return {
          ...state,
          appInstallPrompt: e
        };
      });

      e.userChoice.then((choiceResult) => {
        //sending segment events from here seems to not work
        const message = `${prefix} ${choiceResult.outcome}`
        const event = createClickedSegmentEvent(message)
        analytics.track(event.event, event.properties);
        console.log(message)
        setContext((state) => {
          return {
            ...state,
            appInstallPrompt: false
          };
        });
      });
    };

    if (!context.appInstallPrompt && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.navigator.standalone && window.matchMedia('(display-mode: browser)').matches) {
      const iOSInstallPrompt = () => {
        navigationDispatch({
          type: 'setIosInstallPromptState',
          payload: {
            iosInstallpromptState: {
              isOpen: true
            }
          }
        })
      }
      setContext((state) => {
        return {
          ...state,
          appInstallPrompt: { prompt: iOSInstallPrompt, isIosPrompt: true }
        };
      });
    }

    const hideNavigatingState = () => { toggleNavigatingState(false) }
    const showNavigatingState = () => { toggleNavigatingState(true) }


    if ("serviceWorker" in navigator) {
      window.addEventListener("load", setupServiceWorker);
    }

    window.addEventListener("beforeinstallprompt", beforeInstallPrompt);
    window.addEventListener("pageshow", hideNavigatingState);



    if (typeof (navigation) !== 'undefined') {
      //only works on chrome
      navigation.onnavigate = (e) => {
        if (e.navigationType == 'push') {
          showNavigatingState();
        }

      }
    }

    return () => {
      window.removeEventListener("load", setupServiceWorker);
      window.removeEventListener("beforeinstallprompt", beforeInstallPrompt);
      window.removeEventListener("pageshow", hideNavigatingState);
    }

  }, []);


  const promptAppInstall = () => {
    const eventElement = `${prefix}${context.appInstallPrompt.isIosPrompt ? ' iOS ' : ' '}engaged`;
    const event = createClickedSegmentEvent(eventElement)
    analytics.track(event.event, event.properties)
    console.log(eventElement)
    context.appInstallPrompt?.prompt();
  }

  return (
    <TrackingContext.Provider
      value={{
        ...context,
        setGoogleAnalyticsReady,
        setBloomReachReady,
        setSegmentReady,
        setGa4SessionData,
        setGa4ContentGroup,
        bloomreachInitialPageView,
        bloomreachVirtualPageView,
        bloomreachEvent,
        promptAppInstall,
        toggleNavigatingState,
        isNavigating,
      }}
    >
      <>
        {children}
      </>

    </TrackingContext.Provider>
  );
};

export { TrackingProvider, TrackingContext };
